import React from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { FormLayout, PermissionGuard } from '@/components/layout';
import type { CreateEmployeeAddressData } from '@/schemas/employee/createEmployeeForm';
import { normalizeSelectToNumber, normalizeWatchValueToString } from '@/utils/formHelpers';


interface CreateEmployeeAddressDataFormProps {
  addressForm: {
    register: UseFormRegister<CreateEmployeeAddressData>;
    setValue: UseFormSetValue<CreateEmployeeAddressData>;
    watch: UseFormWatch<CreateEmployeeAddressData>;
    errors: FieldErrors<CreateEmployeeAddressData>;
    trigger: UseFormTrigger<CreateEmployeeAddressData>;
  };
  regionOptions: { value: string; label: string }[];
  communeOptions: { value: string; label: string }[];
  workRegionOptions: { value: string; label: string }[];
  workCommuneOptions: { value: string; label: string }[];
  onCancel: () => void;
  onCreate: () => Promise<void>;
  creating?: boolean;
  onPrevious?: () => void;
}

export const CreateEmployeeAddressDataForm: React.FC<CreateEmployeeAddressDataFormProps> = ({
  addressForm,
  regionOptions,
  communeOptions,
  workRegionOptions = [],
  workCommuneOptions = [],
  onCancel,
  onCreate,
  creating = false,
  onPrevious,
}) => {
  const { t: translate } = useTranslation('employee');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void onCreate();
  };

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      <FormLayout.Section title={translate('addressForm.personalSection')}>
        {/* Row 1: Región, Comuna */}
        <Selector
          label={translate('addressForm.region')}
          value={addressForm.watch('regionId') ? String(addressForm.watch('regionId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            addressForm.setValue('regionId', numericValue || null, { shouldDirty: true });
            // Reset commune when region changes
            if (!numericValue) {
              addressForm.setValue('communeId', null, { shouldDirty: true });
            }
          }}
          options={regionOptions}
          placeholder={translate('addressForm.regionPlaceholder')}
          color={addressForm.errors.regionId ? 'error' : undefined}
          supportingText={addressForm.errors.regionId?.message}
        />
        <Selector
          label={translate('addressForm.commune')}
          value={addressForm.watch('communeId') ? String(addressForm.watch('communeId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            addressForm.setValue('communeId', numericValue || null, { shouldDirty: true });
          }}
          options={communeOptions}
          placeholder={translate('addressForm.communePlaceholder')}
          disabled={!addressForm.watch('regionId')}
          color={addressForm.errors.communeId ? 'error' : undefined}
          supportingText={addressForm.errors.communeId?.message}
        />

        {/* Row 2: Dirección (spans 4 columns) */}
        <LabelInput
          className="md:col-span-4"
          label={translate('addressForm.address')}
          value={normalizeWatchValueToString(addressForm.watch('address'))}
          onChange={(value) => addressForm.setValue('address', value, { shouldDirty: true })}
          placeholder={translate('addressForm.addressPlaceholder')}
          color={addressForm.errors.address ? 'error' : undefined}
          supportingText={addressForm.errors.address?.message}
        />
      </FormLayout.Section>

      <FormLayout.Section title={translate('addressForm.workSection')}>
        {/* Row 1: Región, Comuna */}
        <Selector
          label={translate('addressForm.region')}
          value={addressForm.watch('workRegionId') ? String(addressForm.watch('workRegionId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            addressForm.setValue('workRegionId', numericValue || null, { shouldDirty: true });
            // Reset commune when region changes
            if (!numericValue) {
              addressForm.setValue('workCommuneId', null, { shouldDirty: true });
            }
          }}
          options={workRegionOptions}
          placeholder={translate('addressForm.regionPlaceholder')}
          color={addressForm.errors.workRegionId ? 'error' : undefined}
          supportingText={addressForm.errors.workRegionId?.message}
        />
        <Selector
          label={translate('addressForm.commune')}
          value={addressForm.watch('workCommuneId') ? String(addressForm.watch('workCommuneId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeSelectToNumber(value);
            addressForm.setValue('workCommuneId', numericValue || null, { shouldDirty: true });
          }}
          options={workCommuneOptions}
          placeholder={translate('addressForm.communePlaceholder')}
          disabled={!addressForm.watch('workRegionId')}
          color={addressForm.errors.workCommuneId ? 'error' : undefined}
          supportingText={addressForm.errors.workCommuneId?.message}
        />

        {/* Row 2: Dirección (spans 4 columns) */}
        <LabelInput
          className="md:col-span-4"
          label={translate('addressForm.address')}
          value={normalizeWatchValueToString(addressForm.watch('workAddress'))}
          onChange={(value) => addressForm.setValue('workAddress', value, { shouldDirty: true })}
          placeholder={translate('addressForm.addressPlaceholder')}
          color={addressForm.errors.workAddress ? 'error' : undefined}
          supportingText={addressForm.errors.workAddress?.message}
        />
      </FormLayout.Section>

      <FormLayout.Footer
        cancelButton={
          <PrimaryButton type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </PrimaryButton>
        }
        secondaryButton={
          onPrevious ? (
            <PrimaryButton type="button" variant="outline" onClick={onPrevious}>
              Anterior
            </PrimaryButton>
          ) : undefined
        }
        primaryButton={
          <PermissionGuard action="create">
            <PrimaryButton 
              type="button" 
              onClick={async () => {
                const isValid = await addressForm.trigger();
                if (isValid) await onCreate();
              }}
              isLoading={creating}
            >
              Crear colaborador
            </PrimaryButton>
          </PermissionGuard>
        }
      />
    </FormLayout>
  );
};

export default CreateEmployeeAddressDataForm;
